import React from "react";
import { Waves, Landmark, UtensilsCrossed, Trees, Users, Music, Sparkles } from "lucide-react";
import api from "@/lib/api";
import useFreshData from "@/lib/useFreshData";

const CATEGORIES = {
    deporte:     { label: "Deporte",     Icon: Waves,          color: "#0369A1" },
    cultura:     { label: "Cultura",     Icon: Landmark,       color: "#7C3AED" },
    gastronomia: { label: "Gastronomía", Icon: UtensilsCrossed, color: "#C2410C" },
    naturaleza:  { label: "Naturaleza",  Icon: Trees,          color: "#166534" },
    familia:     { label: "Familia",     Icon: Users,          color: "#B45309" },
    ocio:        { label: "Ocio",        Icon: Music,          color: "#BE185D" },
    otro:        { label: "Plan",        Icon: Sparkles,       color: "#082050" },
};

function catInfo(k) {
    return CATEGORIES[(k || "otro").toLowerCase()] || CATEGORIES.otro;
}

export default function WeeklyPlans() {
    const { data, loading } = useFreshData(
        async () => {
            const r = await api.get("/weekly-plans");
            return r.data || { plans: [], generated_at: null };
        },
        { initialData: { plans: [], generated_at: null } }
    );
    const view = data || { plans: [], generated_at: null };

    if (!loading && (!view.plans || view.plans.length === 0)) return null;

    return (
        <section
            id="que-hacer"
            data-testid="weekly-plans-section"
            className="bg-[#F8F9FA] border-y border-[#E2E4E9]"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
                <div className="max-w-2xl mb-14">
                    <div className="text-xs uppercase tracking-widest text-[#082050]/70 mb-4">
                        Qué hacer en Tarifa
                    </div>
                    <h2 className="text-3xl sm:text-4xl font-semibold text-[#082050] tracking-tight">
                        Qué hacer en Tarifa esta semana.
                    </h2>
                    <p className="mt-4 text-[#4A5568] text-lg">
                        Agenda semanal con planes, actividades y experiencias
                        recomendadas para vivir Tarifa al máximo.
                    </p>
                </div>

                {loading ? (
                    <div data-testid="weekly-plans-loading" className="text-[#4A5568]">
                        Cargando agenda...
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {view.plans.map((p, i) => {
                            const { Icon, label, color } = catInfo(p.category);
                            return (
                                <article
                                    key={i}
                                    data-testid={`weekly-plan-${i}`}
                                    className="bg-white border border-[#E2E4E9] p-6 flex flex-col hover:border-[#082050] transition-colors"
                                >
                                    <div className="flex items-center justify-between">
                                        <span
                                            className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-medium border px-2.5 py-1"
                                            style={{ color, borderColor: `${color}33` }}
                                        >
                                            <Icon size={11} />
                                            {label}
                                        </span>
                                        {p.emoji && (
                                            <span className="text-xl leading-none" aria-hidden>
                                                {p.emoji}
                                            </span>
                                        )}
                                    </div>
                                    {p.date_label && (
                                        <div className="mt-4 text-xs text-[#718096]">
                                            {p.date_label}
                                        </div>
                                    )}
                                    <h3 className="mt-1 text-lg font-semibold text-[#082050] tracking-tight leading-snug">
                                        {p.title}
                                    </h3>
                                    <p className="mt-3 text-sm text-[#4A5568] leading-relaxed flex-1">
                                        {p.description}
                                    </p>
                                </article>
                            );
                        })}
                    </div>
                )}

                {view.generated_at && (
                    <div className="mt-8 text-xs text-[#718096]">
                        Agenda actualizada cada lunes.
                    </div>
                )}
            </div>
        </section>
    );
}
